import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, EventEmitter, Input, OnInit, Output, Self } from '@angular/core';
import { ControlValueAccessor, NgControl, Validators, ValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms';
import { PopoverController } from '@ionic/angular';

@Component({
  selector: 'rfr-input-password',
  templateUrl: './rfr-input-password.component.html',
  styleUrls: ['./rfr-input-password.component.scss'],
  animations: [
    trigger('tooltipState', [
      state('show', style({
        opacity: 1,
        visibility: 'visible'
      })),
      state('hide', style({
        opacity: 0,
        visibility: 'hidden'
      })),
      transition('show => hide', animate('300ms ease-out')),
      transition('hide => show', animate('300ms ease-in'))
    ])
  ]
})
export class RfrInputPasswordComponent implements OnInit, ControlValueAccessor {
  type: string = 'password';
  @Input() labelText: string = '';
  @Input() placeholder: string = '';
  @Input() pattern: string = '';
  @Input() required: boolean = false;
  @Input() readonly: boolean = false;
  @Input() errorMessage: string = '';
  @Input() error: boolean = false;
  @Input() min!: number;
  @Input() max!: number;
  @Input() maxLength: number = 255;
  @Input() style: any = '';
  @Input() class = '';
  @Input() showTooltip = false;
  @Input() validateTooltip = false; // Input to control tooltip visibility
  @Output() blur: EventEmitter<void> = new EventEmitter<void>();
  @Output() onErrors: EventEmitter<any> = new EventEmitter<any>();
  disabled!: boolean;

  // Validation rules
  passwordValidations: any[] = [
    { text: 'atLeastSpecialCharacter', isValid: false },
    { text: 'atLeastUppercaseLetter', isValid: false },
    { text: 'atLeastLowercaseLetter', isValid: false },
    { text: 'atLeastNumber', isValid: false },
    { text: 'minimumCharacter', isValid: false },
    { text: 'passwordShouldNotContainSpaces', isValid: false },
  ];

  tooltipState: 'show' | 'hide' = 'hide';

  onChange: (value: any) => void = () => { };
  onTouched: () => void = () => { };

  constructor(@Self() public controlDir: NgControl, private popoverController: PopoverController) {
    controlDir.valueAccessor = this;
  }

  ngOnInit(): void {
    const control = this.controlDir.control;
    if (control?.value) {
      this.validatePassword(control.value);
    }
    this.updateValidators(); // Initial validation setup
    control?.updateValueAndValidity();
  }

  writeValue(value: any): void {
    
   }

  registerOnChange(onChange: (value: any) => void): void {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
    this.controlDir.control?.markAsTouched();
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  onBlur(): void {
    this.onTouched(); // Manually trigger the onTouched method
    this.blur.emit(); // Emit the blur event
    this.tooltipState = 'hide';
  }

  onFocus(): void {
    this.tooltipState = 'show';
  }

  onChangeInput(value: any): void {
    this.onChange(value); // Forward the input value change to the onChange method
    this.validatePassword(value);
    this.validateFormControl();

    const invalidValidations = this.passwordValidations;
    const errorMessages = invalidValidations ? invalidValidations : [];
    this.onErrors.emit(errorMessages);
  }

  onShowPassword(): void {
    this.type = this.type === 'text' ? 'password' : 'text';
  }

  private validatePassword(value: string): void {
    // Reset all validations
    this.passwordValidations.forEach(validation => validation.isValid = false);

    // Check each validation rule
    if (value.length >= 8) {
      this.setValidationStatus('minimumCharacter', true);
    }
    if (/[a-z]/.test(value)) {
      this.setValidationStatus('atLeastLowercaseLetter', true);
    }
    if (/[A-Z]/.test(value)) {
      this.setValidationStatus('atLeastUppercaseLetter', true);
    }
    if (/\d/.test(value)) {
      this.setValidationStatus('atLeastNumber', true);
    }
    if (!/\s/.test(value)) {
      this.setValidationStatus('passwordShouldNotContainSpaces', true);
    }
    if (/[ $@!*?&#]/.test(value)) {
      this.setValidationStatus('atLeastSpecialCharacter', true);
    }

    this.updateValidators();
  }

  private setValidationStatus(validationText: string, isValid: boolean): void {
    const validation = this.passwordValidations.find(item => item.text === validationText);
    
    if (validation) {
      validation.isValid = isValid;
    }
  }

  private passwordValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (this.passwordValidations.some(validation => !validation.isValid)) {
        return { invalidPassword: true };
      }
      return null;
    };
  }

  private validateFormControl(): void {
    const control = this.controlDir.control;
    if (control) {
      control.updateValueAndValidity();
    }
  }

  ngOnDestroy(): void {
    this.controlDir.control?.clearValidators();
  }

  private updateValidators(): void {
    const control = this.controlDir.control;
    let validators: any;

    if (this.required) {
      validators = control?.validator
        ? [control.validator, Validators.required]
        : Validators.required;
    }

    if (this.validateTooltip && this.passwordValidations.some(validation => !validation.isValid)) {
      validators.push(this.passwordValidator());
    }

    control?.setValidators(validators.length > 0 ? validators : null);
  }
}
